import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import client from '../../api/client';

const STATUS_CONFIG = {
  'Open':        { badge: 'bg-blue-100 text-blue-700',     dot: 'bg-blue-400' },
  'Ticket Assignment': { badge: 'bg-purple-100 text-purple-700', dot: 'bg-purple-400' },
  'Maintenance': { badge: 'bg-amber-100 text-amber-700',   dot: 'bg-amber-500' },
  'Resolved':    { badge: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500' },
  'Closed':      { badge: 'bg-gray-100 text-gray-500',     dot: 'bg-gray-400' },
};

function StatusBadge({ status, isOverdue }) {
  const cfg = STATUS_CONFIG[status] || { badge: 'bg-gray-100 text-gray-500', dot: 'bg-gray-400' };
  return (
    <span className="flex items-center gap-1.5 flex-wrap">
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold ${cfg.badge}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
        {status}
      </span>
      {isOverdue && status !== 'Closed' && (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-600">
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" /></svg>
          Overdue
        </span>
      )}
    </span>
  );
}

function getFYOptions() {
  const today = new Date();
  const year = today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
  return [
    { value: `${String(year).slice(2)}${String(year + 1).slice(2)}`, label: `FY ${year}-${year + 1}` },
    { value: `${String(year + 1).slice(2)}${String(year + 2).slice(2)}`, label: `FY ${year + 1}-${year + 2}` },
    { value: `${String(year - 1).slice(2)}${String(year).slice(2)}`, label: `FY ${year - 1}-${year}` },
  ];
}

export default function PMTickets() {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user.role === 'ADMIN';
  const FY_OPTIONS = getFYOptions();

  const [tickets, setTickets]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [creating, setCreating]     = useState(false);
  const [toast, setToast]           = useState(null);

  // Filters
  const [filterHalf, setFilterHalf]         = useState('');
  const [filterFY, setFilterFY]             = useState('');
  const [filterLocation, setFilterLocation] = useState('');
  const [filterStatus, setFilterStatus]     = useState('');

  // Batch-create form
  const [batchHalf, setBatchHalf] = useState('H1');
  const [batchFY, setBatchFY]     = useState(FY_OPTIONS[0].value);

  // Location picker state
  const [locationStatus, setLocationStatus]   = useState([]);   // [{id, name, total_rooms, existing_tickets, fully_covered}]
  const [selectedLocs, setSelectedLocs]        = useState(new Set());
  const [locStatusLoading, setLocStatusLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterHalf)     params.half        = filterHalf;
      if (filterFY)       params.fiscal_year = filterFY;
      if (filterLocation) params.location    = filterLocation;
      if (filterStatus)   params.status      = filterStatus;
      const { data } = await client.get('/tickets/pm-list/', { params });
      setTickets(data);
    } catch { setTickets([]); }
    finally { setLoading(false); }
  }, [filterHalf, filterFY, filterLocation, filterStatus]);

  useEffect(() => { load(); }, [load]);

  // Fetch location status whenever period changes
  useEffect(() => {
    if (!isAdmin) return;
    setLocStatusLoading(true);
    client.get('/tickets/pm-location-status/', { params: { fiscal_year: batchFY, half: batchHalf } })
      .then(({ data }) => {
        setLocationStatus(data);
        // Pre-select all locations that are NOT fully covered
        setSelectedLocs(new Set(data.filter(l => !l.fully_covered).map(l => l.id)));
      })
      .catch(() => setLocationStatus([]))
      .finally(() => setLocStatusLoading(false));
  }, [batchFY, batchHalf, isAdmin]);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 5000);
  };

  const handleCreate = async () => {
    if (selectedLocs.size === 0) {
      showToast('Select at least one location.', 'error');
      return;
    }
    setCreating(true);
    try {
      const { data } = await client.post('/tickets/pm-batch-create/', {
        fiscal_year: batchFY,
        half: batchHalf,
        location_ids: Array.from(selectedLocs),
      });
      showToast(`Created ${data.created} PM ticket${data.created !== 1 ? 's' : ''}${data.skipped ? `, skipped ${data.skipped} already existing` : ''}.`);
      load();
      // Refresh location status
      client.get('/tickets/pm-location-status/', { params: { fiscal_year: batchFY, half: batchHalf } })
        .then(({ data: ls }) => {
          setLocationStatus(ls);
          setSelectedLocs(new Set(ls.filter(l => !l.fully_covered).map(l => l.id)));
        }).catch(() => {});
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to create PM tickets.', 'error');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this PM ticket?')) return;
    try {
      await client.delete(`/tickets/${id}/`);
      setTickets(prev => prev.filter(t => t.id !== id));
      showToast('Ticket deleted.');
    } catch { showToast('Could not delete ticket.', 'error'); }
  };

  const toggleLoc = (id) => {
    setSelectedLocs(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectableLocs = locationStatus.filter(l => !l.fully_covered);
  const allSelected    = selectableLocs.length > 0 && selectableLocs.every(l => selectedLocs.has(l.id));

  const toggleAll = () => {
    if (allSelected) {
      setSelectedLocs(new Set());
    } else {
      setSelectedLocs(new Set(selectableLocs.map(l => l.id)));
    }
  };

  const fyLabel = (fy) => {
    if (!fy) return '—';
    return `FY ${20}${fy.slice(0, 2)}-${20}${fy.slice(2)}`;
  };

  const overdueCount = tickets.filter(t => t.is_overdue).length;
  const openCount    = tickets.filter(t => t.status !== 'Closed').length;
  const closedCount  = tickets.filter(t => t.status === 'Closed').length;

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">

      {toast && (
        <div className={`fixed top-5 right-5 z-50 flex items-center gap-3 px-5 py-3 rounded-2xl shadow-xl text-sm font-semibold transition-all ${
          toast.type === 'error' ? 'bg-red-500 text-white' : 'bg-emerald-500 text-white'
        }`}>
          {toast.type === 'error'
            ? <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/></svg>
            : <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"/></svg>
          }
          {toast.msg}
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">PM Tickets</h1>
          <p className="text-sm text-gray-400 mt-0.5">{tickets.length} total · {openCount} open · {closedCount} closed{overdueCount > 0 ? ` · ${overdueCount} overdue` : ''}</p>
        </div>
      </div>

      {/* Admin: Batch Create with location picker */}
      {isAdmin && (
        <div className="card p-5 space-y-5">
          <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wide">Create PM Tickets</h2>

          {/* Period selectors */}
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1.5">Financial Year</label>
              <select value={batchFY} onChange={e => setBatchFY(e.target.value)} className="input-field w-44">
                {FY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1.5">Half</label>
              <select value={batchHalf} onChange={e => setBatchHalf(e.target.value)} className="input-field w-36">
                <option value="H1">H1 (Apr – Sep)</option>
                <option value="H2">H2 (Oct – Mar)</option>
              </select>
            </div>
          </div>

          {/* Location checkboxes */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Select Locations</span>
              {selectableLocs.length > 0 && (
                <button onClick={toggleAll} className="text-xs text-brand-500 hover:text-magenta-500 font-semibold">
                  {allSelected ? 'Deselect All' : 'Select All'}
                </button>
              )}
            </div>

            {locStatusLoading ? (
              <div className="flex items-center gap-2 text-sm text-gray-400 py-2">
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>
                Loading locations…
              </div>
            ) : locationStatus.length === 0 ? (
              <p className="text-sm text-gray-400">No active locations found.</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                {locationStatus.map(loc => {
                  const disabled = loc.fully_covered;
                  const checked  = selectedLocs.has(loc.id);
                  const partial  = !disabled && loc.existing_tickets > 0;
                  return (
                    <label
                      key={loc.id}
                      className={`flex items-start gap-2.5 p-3 rounded-xl border cursor-pointer transition-all text-sm select-none ${
                        disabled
                          ? 'bg-gray-50 border-gray-100 opacity-50 cursor-not-allowed'
                          : checked
                            ? 'bg-brand-50 border-brand-300'
                            : 'bg-white border-gray-200 hover:border-brand-200'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={disabled}
                        onChange={() => !disabled && toggleLoc(loc.id)}
                        className="mt-0.5 accent-magenta-500 cursor-pointer"
                      />
                      <span className="flex-1 min-w-0">
                        <span className={`font-medium block truncate ${disabled ? 'text-gray-400' : 'text-gray-700'}`}>
                          {loc.name}
                        </span>
                        {disabled ? (
                          <span className="text-xs text-emerald-600 font-semibold">All rooms done ✓</span>
                        ) : partial ? (
                          <span className="text-xs text-amber-600">{loc.existing_tickets}/{loc.total_rooms} rooms done</span>
                        ) : (
                          <span className="text-xs text-gray-400">{loc.total_rooms} room{loc.total_rooms !== 1 ? 's' : ''}</span>
                        )}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          {/* Create button */}
          <div className="flex items-center gap-4 pt-1 border-t border-gray-100">
            <button
              onClick={handleCreate}
              disabled={creating || selectedLocs.size === 0}
              className="btn-primary flex items-center gap-2 px-5 py-2.5 disabled:opacity-60"
            >
              {creating
                ? <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>
                : <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4"/></svg>
              }
              {creating ? 'Creating…' : `Create PM Tickets${selectedLocs.size > 0 ? ` (${selectedLocs.size} location${selectedLocs.size !== 1 ? 's' : ''})` : ''}`}
            </button>
            <p className="text-xs text-gray-400">One ticket per active room · Skips existing · Assigns to location RE</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card p-4">
        <div className="flex flex-wrap gap-3">
          <select value={filterFY} onChange={e => setFilterFY(e.target.value)} className="input-field w-44">
            <option value="">All Years</option>
            {FY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <select value={filterHalf} onChange={e => setFilterHalf(e.target.value)} className="input-field w-36">
            <option value="">All Halves</option>
            <option value="H1">H1 (Apr–Sep)</option>
            <option value="H2">H2 (Oct–Mar)</option>
          </select>
          <select value={filterLocation} onChange={e => setFilterLocation(e.target.value)} className="input-field w-52">
            <option value="">All Locations</option>
            {locationStatus.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="input-field w-44">
            <option value="">All Statuses</option>
            {['Open', 'Ticket Assignment', 'Maintenance', 'Resolved', 'Closed'].map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          {(filterFY || filterHalf || filterLocation || filterStatus) && (
            <button onClick={() => { setFilterFY(''); setFilterHalf(''); setFilterLocation(''); setFilterStatus(''); }}
              className="text-xs text-brand-500 hover:text-magenta-500 font-medium self-center">
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <svg className="w-6 h-6 animate-spin text-brand-400" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
            </svg>
          </div>
        ) : tickets.length === 0 ? (
          <div className="text-center py-20">
            <svg className="w-12 h-12 text-gray-200 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" /></svg>
            <p className="text-sm text-gray-400">No PM tickets found.</p>
            {isAdmin && <p className="text-xs text-gray-300 mt-1">Use the form above to create PM tickets for a half-year.</p>}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-brand-100">
                <th className="text-left px-4 py-3 text-xs font-bold text-brand-400 uppercase tracking-wide">Ticket #</th>
                <th className="text-left px-4 py-3 text-xs font-bold text-brand-400 uppercase tracking-wide">Location</th>
                <th className="text-left px-4 py-3 text-xs font-bold text-brand-400 uppercase tracking-wide">Room</th>
                <th className="text-left px-4 py-3 text-xs font-bold text-brand-400 uppercase tracking-wide">Assigned To</th>
                <th className="text-left px-4 py-3 text-xs font-bold text-brand-400 uppercase tracking-wide">Half / Year</th>
                <th className="text-left px-4 py-3 text-xs font-bold text-brand-400 uppercase tracking-wide">Due Date</th>
                <th className="text-left px-4 py-3 text-xs font-bold text-brand-400 uppercase tracking-wide">Status</th>
                {isAdmin && <th className="px-4 py-3" />}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {tickets.map((t, i) => (
                <tr key={t.id} className={`group hover:bg-brand-50/40 transition-colors ${i % 2 === 0 ? '' : 'bg-gray-50/40'}`}>
                  <td className="px-4 py-3">
                    <Link to={`/tickets/${t.id}`} className="text-brand-500 hover:text-magenta-500 font-semibold text-xs">
                      {t.ticket_number}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-gray-700">{t.room_detail?.location?.name || '—'}</td>
                  <td className="px-4 py-3 text-gray-700">{t.room_detail?.room_name || '—'}</td>
                  <td className="px-4 py-3 text-gray-600">{t.assigned_to_name || <span className="text-gray-300 italic">Unassigned</span>}</td>
                  <td className="px-4 py-3">
                    {t.pm_half ? (
                      <span className="text-xs font-semibold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full">
                        {t.pm_half} · {fyLabel(t.pm_fiscal_year)}
                      </span>
                    ) : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-sm font-medium ${t.is_overdue && t.status !== 'Closed' ? 'text-red-500' : 'text-gray-600'}`}>
                      {t.due_date ? new Date(t.due_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                    </span>
                  </td>
                  <td className="px-4 py-3"><StatusBadge status={t.status} isOverdue={t.is_overdue} /></td>
                  {isAdmin && (
                    <td className="px-4 py-3">
                      <button onClick={() => handleDelete(t.id)}
                        className="opacity-0 group-hover:opacity-100 text-red-300 hover:text-red-500 transition-all p-1 rounded" title="Delete ticket">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
